<template>
  <div>
    <div class="text-subtitle-1">転記ルール</div>
    <div class="text-caption text-grey-darken-1">
      上位計画の項目を下位計画に引き継ぐ設定です。転記先のレイアウトごとに設定します。
    </div>

    <!--
      隣接ペア (全体→年間→月案→週案) は3通りしかないので、まず組み合わせを選ぶ。
      種別が決まれば左右のレイアウトは名前だけで選べるようになり、
      転記元・転記先が対称に並ぶ。
    -->
    <v-row align="center" class="mt-2">
      <v-col cols="12" md="5">
        <v-select
          :model-value="pairTargetPlanType"
          :items="pairItems"
          label="転記の組み合わせ"
          variant="outlined"
          density="compact"
          color="primary"
          hide-details
          :disabled="saving"
          @update:model-value="onPairChange"
        ></v-select>
      </v-col>
      <v-col cols="12" md="7" class="d-flex align-center">
        <v-spacer />
        <v-btn
          v-if="targetTemplateId"
          size="small"
          variant="text"
          color="primary"
          @click="$emit('open-template', targetTemplateId)"
        >
          <v-icon start>mdi-table-edit</v-icon>
          転記先のレイアウトを編集
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-alert v-else-if="loadFailed" type="error" variant="tonal" density="compact" class="mt-3">
      転記ルールを読み込めませんでした。通信状況を確認して、もう一度お試しください。
      <template #append>
        <v-btn size="small" variant="outlined" @click="load">再読み込み</v-btn>
      </template>
    </v-alert>

    <template v-else-if="targetTemplateId">
      <v-alert v-if="!targetLayout" type="info" variant="tonal" density="compact" class="mt-3">
        このレイアウトはまだ公開されていません。公開すると転記ルールを設定できます。
      </v-alert>

      <template v-else>
        <!-- 操作の説明と選択状態 -->
        <v-card variant="outlined" class="mt-3 pa-3 tp-rule-bar">
          <div class="d-flex align-center flex-wrap ga-3">
            <div class="tp-rule-bar__slot">
              <div class="text-caption text-grey-darken-1">転記元の項目</div>
              <div v-if="barSourceCell" class="tp-rule-bar__value">
                <span v-if="showingActiveRule && selectedRule" class="tp-legend tp-legend--carry mr-1">
                  {{ selectedRule.symbol }}
                </span>
                {{ barSourceCell.name }}
              </div>
              <div v-else class="tp-rule-bar__placeholder">左のグリッドから選択</div>
              <div
                v-if="barSourceKey && missingSourceLabel(barSourceKey)"
                class="text-caption text-warning mt-1"
              >
                {{ missingSourceLabel(barSourceKey) }}
              </div>
            </div>

            <v-icon color="grey">mdi-arrow-right-bold</v-icon>

            <div class="tp-rule-bar__slot">
              <div class="text-caption text-grey-darken-1">転記先の項目</div>
              <div v-if="barTargetCell" class="tp-rule-bar__value">
                {{ barTargetCell.name }}
              </div>
              <div v-else class="tp-rule-bar__placeholder">右のグリッドから選択</div>
            </div>

            <v-divider vertical></v-divider>

            <div class="tp-rule-bar__slot">
              <div class="text-caption text-grey-darken-1">種別</div>
              <template v-if="derivedMode">
                <v-chip size="small" variant="flat" :color="derivedMode === 'reference' ? 'primary' : 'light-blue-lighten-4'">
                  {{ transferModeLabels[derivedMode] }}
                </v-chip>
                <div class="text-caption text-grey-darken-1 mt-1">{{ derivedModeHint }}</div>
              </template>
              <div v-else class="tp-rule-bar__placeholder">転記先の型から自動で決まります</div>
            </div>

            <v-spacer />

            <v-btn
              v-if="selectedSourceKey || selectedTargetKey"
              size="small"
              variant="text"
              @click="clearSelection"
            >
              選択を解除
            </v-btn>
            <v-btn
              v-if="activeRule"
              size="small"
              variant="text"
              color="error"
              @click="removeRule(activeRule.targetItemKey)"
            >
              <v-icon start>mdi-link-variant-off</v-icon>
              {{ activeRule.symbol }} の対応を解除
            </v-btn>
          </div>
          <div class="text-caption text-grey-darken-1 mt-2">
            転記元と転記先を1つずつ選ぶと対応づきます。記号（A・B・C…）が両側の同じ項目に付きます。
          </div>
        </v-card>

        <!-- マトリクスビルダー -->
        <v-row class="mt-1">
          <v-col cols="12" md="6">
            <div class="text-subtitle-2 mb-1">
              転記元: {{ planTypeLabels[sourcePlanType] }}
            </div>
            <v-select
              v-model="sourceTemplateId"
              :items="sourceTemplateItems"
              label="転記元のレイアウト"
              variant="outlined"
              density="compact"
              color="primary"
              hide-details
              class="mb-2"
              no-data-text="公開済みのレイアウトがありません"
            ></v-select>
            <teaching-plan-mini-grid
              :layout="sourceLayout"
              :markers="sourceMarkers"
              :selected-item-key="selectedSourceKey"
              :active-item-key="activeSourceKey"
              empty-text="公開済みの上位レイアウトがありません。先に上位の帳票レイアウトを公開してください。"
              @select="selectSource"
            ></teaching-plan-mini-grid>
          </v-col>

          <v-col cols="12" md="6">
            <div class="d-flex align-center mb-1">
              <div class="text-subtitle-2">
                転記先: {{ planTypeLabels[targetPlanType] }}
              </div>
              <v-spacer />
              <div class="text-caption text-grey-darken-1">
                {{ draftRules.length }} / {{ targetCells.length }} 項目に設定済み
              </div>
            </div>
            <v-select
              :model-value="targetTemplateId"
              :items="targetTemplateItems"
              label="転記先のレイアウト"
              variant="outlined"
              density="compact"
              color="primary"
              hide-details
              class="mb-2"
              :disabled="saving"
              no-data-text="公開済みのレイアウトがありません"
              @update:model-value="onTargetTemplateChange"
            ></v-select>
            <teaching-plan-mini-grid
              :layout="targetLayout"
              :markers="targetMarkers"
              :selected-item-key="selectedTargetKey"
              :active-item-key="activeTargetKey"
              :muted-item-keys="selectedSourceKey ? mappedTargetKeys : []"
              @select="selectTarget"
            ></teaching-plan-mini-grid>
          </v-col>
        </v-row>

        <div class="d-flex align-center flex-wrap ga-3 mt-2 text-caption text-grey-darken-1">
          <span class="d-inline-flex align-center ga-1">
            <span class="tp-legend tp-legend--carry">A</span>
            引き込み（作成時にコピーし以後は個別に編集）
          </span>
          <span class="d-inline-flex align-center ga-1">
            <span class="tp-legend tp-legend--reference">A</span>
            参照（常に上位の値を映す・編集不可）
          </span>
          <span class="d-inline-flex align-center ga-1">
            <span class="tp-legend tp-legend--broken">A</span>
            要確認
          </span>
        </div>

        <!-- 設定済みルール -->
        <div class="mt-5 d-flex align-center flex-wrap ga-2">
          <div class="text-subtitle-2">設定済みのルール（{{ draftRules.length }}件）</div>
          <v-checkbox
            v-if="brokenCount > 0"
            v-model="brokenOnly"
            label="要確認のみ表示"
            density="compact"
            color="warning"
            hide-details
          ></v-checkbox>
          <v-spacer />
          <span v-if="isDirty" class="text-caption text-warning mr-3">
            <v-icon size="14">mdi-circle-medium</v-icon>
            未保存の変更があります
          </span>
          <v-btn
            color="primary"
            variant="flat"
            size="small"
            :loading="saving"
            :disabled="!isDirty || loading || loadedTemplateId !== targetTemplateId"
            @click="save"
          >
            保存する
          </v-btn>
        </div>

        <v-table density="compact" class="mt-2">
          <thead>
            <tr>
              <th style="width: 72px">記号</th>
              <th>転記元の項目</th>
              <th style="width: 110px">種別</th>
              <th>転記先の項目</th>
              <th style="width: 150px">状態</th>
              <th style="width: 80px"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rule in visibleRules"
              :key="rule.targetItemKey"
              class="tp-rule-row"
              :class="{ 'tp-rule-row--active': activeRuleTargetKey === rule.targetItemKey }"
              @click="focusRule(rule.targetItemKey)"
              @mouseenter="hoveredRuleTargetKey = rule.targetItemKey"
              @mouseleave="hoveredRuleTargetKey = null"
            >
              <td>
                <span
                  class="tp-legend"
                  :class="rule.broken
                    ? 'tp-legend--broken'
                    : rule.mode === 'reference'
                      ? 'tp-legend--reference'
                      : 'tp-legend--carry'"
                >
                  {{ rule.symbol }}
                </span>
              </td>
              <td>
                {{ sourceCellName(rule.sourceItemKey) }}
                <!--
                  転記元はレイアウトに属さず項目キーで指定するため、
                  「この項目キーを持つレイアウトはどれか」を出す
                -->
                <div v-if="sourceKeysByTemplate.length > 1" class="text-caption text-grey">
                  {{ sourceScopeLabel(rule.sourceItemKey) }}
                </div>
              </td>
              <td>
                <v-chip size="x-small" variant="flat" :color="rule.mode === 'reference' ? 'primary' : 'light-blue-lighten-4'">
                  {{ transferModeLabels[rule.mode] }}
                </v-chip>
              </td>
              <td>
                <template v-if="isInTargetLayout(rule.targetItemKey)">
                  {{ targetCellName(rule.targetItemKey) }}
                </template>
                <template v-else>
                  <span class="text-grey-darken-1">レイアウトにない項目</span>
                  <div class="text-caption text-grey">{{ rule.targetItemKey }}</div>
                </template>
              </td>
              <td>
                <!-- 詳細は下のまとめに書くので、ここは状態が分かる最小限にする -->
                <div class="d-flex flex-wrap ga-1">
                  <v-chip v-if="rule.broken" size="x-small" variant="flat" color="warning">
                    要確認
                  </v-chip>
                  <v-chip
                    v-if="missingSourceTemplates(rule.sourceItemKey).length > 0"
                    size="x-small"
                    variant="outlined"
                    color="warning"
                    :title="missingSourceLabel(rule.sourceItemKey)"
                  >
                    一部から引けません
                  </v-chip>
                  <span
                    v-if="!rule.broken && missingSourceTemplates(rule.sourceItemKey).length === 0"
                    class="text-caption text-grey"
                  >
                    —
                  </span>
                </div>
              </td>
              <td class="text-right">
                <v-btn
                  size="x-small"
                  variant="text"
                  color="error"
                  @click.stop="removeRule(rule.targetItemKey)"
                >
                  解除
                </v-btn>
              </td>
            </tr>
            <tr v-if="visibleRules.length === 0">
              <td colspan="6" class="text-center text-grey py-4">
                {{
                  brokenOnly
                    ? "要確認のルールはありません。"
                    : "このレイアウトには転記ルールがありません。"
                }}
              </td>
            </tr>
          </tbody>
        </v-table>

        <v-alert v-if="brokenCount > 0" type="warning" variant="tonal" density="compact" class="mt-3">
          {{ brokenCount }}件のルールが「要確認」です。転記先の項目がレイアウトから消えたか、種別が変わっています。
        </v-alert>

        <!--
          転記元はレイアウトを固定せず項目キーで指定する仕様のため、上位が別レイアウトで
          作られていると実行時に引けない。設定時点で気づけるようここで知らせる。
        -->
        <v-alert
          v-if="rulesMissingInSomeSource.length > 0"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          <div class="font-weight-bold">
            {{ rulesMissingInSomeSource.length }}件のルールは、一部の{{ planTypeLabels[sourcePlanType] }}から引けません
          </div>
          <div class="text-caption mt-1">
            転記元はレイアウトを固定せず項目で指定します。上位の帳票が下記のレイアウトで作られていると、
            その項目が無いため転記されません。
          </div>
          <!--
            ルールごとに並べると同じレイアウト名が件数分繰り返されて読めない。
            「どのレイアウトに、どの項目が無いか」でまとめる。
          -->
          <ul class="text-caption mt-1">
            <li v-for="entry in missingBySourceTemplate" :key="entry.templateId">
              <span class="font-weight-bold">{{ entry.name }}</span>
              には
              {{ entry.symbols.join("・") }}
              の項目がありません（{{ entry.itemNames.join("・") }}）
            </li>
          </ul>
        </v-alert>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanTemplateListItem,
  NurseryTeachingPlanTransferRuleItem,
  NurseryTeachingPlanTransferRuleModeType,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import TeachingPlanMiniGrid, {
  TeachingPlanMiniGridMarker,
} from "@/components/teaching-plan/TeachingPlanMiniGrid.vue";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { confirmAction, confirmDiscard, notifySuccess } from "@/utils/TeachingPlanDialog";
import {
  adjacentUpperPlanType,
  planTypeLabels,
  planTypeOrder,
  transferModeLabels,
} from "@/constants/teachingPlan";

type DraftRule = {
  sourceItemKey: string;
  targetItemKey: string;
  mode: NurseryTeachingPlanTransferRuleModeType;
};

type DisplayRule = DraftRule & { symbol: string; broken: boolean };

/**
 * 転記ルールのマトリクスビルダー (renewal-plan.md C-4・api-design.md §2.6)。
 *
 * 転記元・転記先のレイアウトを実際の行×列のまま左右に並べ、セルを1つずつ選んで
 * 対応づける。対応したペアには両側に同じ記号 (A・B・C…) を出すため、
 * 「どの位置の項目がどこへ流れるか」がグリッド上で追える。
 *
 * 転記モードは転記先セルの型で一意に決まる (参照セル=reference / 入力セル=carry) ため、
 * 画面では選ばせずに自動判定する。保存は転記先レイアウト単位の一括置換。
 */
export default defineComponent({
  name: "TeachingPlanTransferRuleTab",

  components: { TeachingPlanMiniGrid },

  props: {
    nurseryId: {
      type: Number,
      required: true,
    },
    templates: {
      type: Array as PropType<NurseryTeachingPlanTemplateListItem[]>,
      default: () => [],
    },
    /** レイアウト編集画面から遷移してきたときの初期選択 */
    initialTargetTemplateId: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
  },

  emits: ["open-template"],

  async mounted() {
    this.initSelection();
  },

  data() {
    return {
      loading: false,
      saving: false,
      /** 読み込みの後着応答を捨てるためのトークン */
      loadToken: 0,
      /** 一覧取得の失敗フラグ。「ルール0件」と区別して編集・保存を封鎖する */
      loadFailed: false,
      /** いま draftRules が対応している転記先レイアウト (保存先の取り違え防止) */
      loadedTemplateId: null as string | null,
      /** 転記の組み合わせ (隣接ペア) は転記先の帳票種別で表す */
      pairTargetPlanType: null as NurseryTeachingPlanType | null,
      targetTemplateId: null as string | null,
      targetLayout: null as NurseryTeachingPlanLayout | null,
      /** 左に描画する転記元レイアウト (上位に複数の公開レイアウトがある場合に切り替える) */
      sourceTemplateId: null as string | null,
      sourceLayouts: {} as { [templateId: string]: NurseryTeachingPlanLayout },
      /** 上位の全公開レイアウトの項目 (ルール名の解決に使う。サーバの検証範囲と同じ) */
      sourceCells: [] as NurseryTeachingPlanLayoutCell[],
      savedRules: [] as NurseryTeachingPlanTransferRuleItem[],
      draftRules: [] as DraftRule[],
      selectedSourceKey: null as string | null,
      selectedTargetKey: null as string | null,
      /** 表で選択中のルール (両グリッドで対を強調する) */
      activeRuleTargetKey: null as string | null,
      hoveredRuleTargetKey: null as string | null,
      brokenOnly: false,
      planTypeLabels,
      transferModeLabels,
    };
  },

  computed: {
    /** 転記の組み合わせ (隣接ペア)。上位がある帳票種別の数だけ = 3通り */
    pairItems(): { title: string; value: NurseryTeachingPlanType }[] {
      return planTypeOrder
        .filter((planType) => adjacentUpperPlanType[planType] !== null)
        .map((planType) => ({
          title: `${planTypeLabels[adjacentUpperPlanType[planType] as NurseryTeachingPlanType]} → ${planTypeLabels[planType]}`,
          value: planType,
        }));
    },
    /** 転記先は選択中の組み合わせの種別に絞る (種別が決まっているので名前だけで選べる) */
    targetTemplateItems(): { title: string; value: string }[] {
      if (this.pairTargetPlanType === null) {
        return [];
      }
      return this.publishedTemplatesOf(this.pairTargetPlanType).map((template) => ({
        title: template.name,
        value: template.templateId,
      }));
    },
    targetTemplate(): NurseryTeachingPlanTemplateListItem | null {
      return (
        this.templates.find((template) => template.templateId === this.targetTemplateId) || null
      );
    },
    targetPlanType(): NurseryTeachingPlanType {
      return this.pairTargetPlanType ?? "monthly";
    },
    sourcePlanType(): NurseryTeachingPlanType {
      return adjacentUpperPlanType[this.targetPlanType] || "overall";
    },
    /** 転記元として描画できる上位の公開レイアウト (種別は組み合わせで決まっている) */
    sourceTemplateItems(): { title: string; value: string }[] {
      return this.publishedTemplatesOf(this.sourcePlanType).map((template) => ({
        title: template.name,
        value: template.templateId,
      }));
    },
    sourceLayout(): NurseryTeachingPlanLayout | null {
      return this.sourceTemplateId ? this.sourceLayouts[this.sourceTemplateId] || null : null;
    },
    /**
     * 上位レイアウトごとの項目キー集合。
     * 転記元はレイアウトを固定せず項目キーで指定する仕様 (api-design.md §2.6) のため、
     * 選んだキーが「どの上位レイアウトに存在するか」で実行時に引けるかが決まる。
     */
    sourceKeysByTemplate(): { templateId: string; name: string; itemKeys: string[] }[] {
      return this.sourceTemplateItems.map((item) => ({
        templateId: item.value,
        name: item.title,
        itemKeys: TeachingPlanUtil.itemKeyOrder(this.sourceLayouts[item.value] || null),
      }));
    },
    targetCells(): NurseryTeachingPlanLayoutCell[] {
      // 見出しセルは転記先にできない
      return TeachingPlanUtil.collectInputCells(this.targetLayout);
    },
    selectedTargetCell(): NurseryTeachingPlanLayoutCell | null {
      return this.targetCells.find((cell) => cell.itemKey === this.selectedTargetKey) || null;
    },
    selectedSourceCell(): NurseryTeachingPlanLayoutCell | null {
      return this.sourceCells.find((cell) => cell.itemKey === this.selectedSourceKey) || null;
    },
    /** 転記先セルの型からモードが決まる */
    derivedMode(): NurseryTeachingPlanTransferRuleModeType | null {
      if (!this.barTargetCell) {
        return null;
      }
      return this.barTargetCell.type === "reference" ? "reference" : "carry";
    },
    derivedModeHint(): string {
      if (this.derivedMode === "reference") {
        return "参照セルなので常に上位の値を映します";
      }
      if (this.derivedMode === "carry") {
        return "作成時に値を引き込み、以後は個別に編集できます";
      }
      return "";
    },
    /** 記号はレイアウト上の転記先の並び順に採番する (画面と表で同じ順序にする) */
    orderedRules(): DisplayRule[] {
      const order = TeachingPlanUtil.itemKeyOrder(this.targetLayout);
      const positionOf = (itemKey: string): number => {
        const index = order.indexOf(itemKey);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
      return [...this.draftRules]
        .sort((a, b) => positionOf(a.targetItemKey) - positionOf(b.targetItemKey))
        .map((rule, index) => ({
          ...rule,
          symbol: TeachingPlanUtil.transferSymbol(index),
          broken: this.isBroken(rule.targetItemKey),
        }));
    },
    visibleRules(): DisplayRule[] {
      return this.brokenOnly ? this.orderedRules.filter((rule) => rule.broken) : this.orderedRules;
    },
    targetMarkers(): { [itemKey: string]: TeachingPlanMiniGridMarker[] } {
      const markers: { [itemKey: string]: TeachingPlanMiniGridMarker[] } = {};
      for (const rule of this.orderedRules) {
        markers[rule.targetItemKey] = [
          { symbol: rule.symbol, mode: rule.mode, broken: rule.broken },
        ];
      }
      return markers;
    },
    /** 転記元は複数の転記先に使えるため、1セルに複数の記号が付きうる */
    sourceMarkers(): { [itemKey: string]: TeachingPlanMiniGridMarker[] } {
      const markers: { [itemKey: string]: TeachingPlanMiniGridMarker[] } = {};
      for (const rule of this.orderedRules) {
        const list = markers[rule.sourceItemKey] || [];
        list.push({ symbol: rule.symbol, mode: rule.mode, broken: rule.broken });
        markers[rule.sourceItemKey] = list;
      }
      return markers;
    },
    mappedTargetKeys(): string[] {
      return this.draftRules.map((rule) => rule.targetItemKey);
    },
    /** 表のホバー・選択で両グリッドの対を強調する */
    activeRule(): DisplayRule | null {
      const key = this.hoveredRuleTargetKey || this.activeRuleTargetKey;
      return this.orderedRules.find((rule) => rule.targetItemKey === key) || null;
    },
    activeTargetKey(): string | null {
      return this.activeRule ? this.activeRule.targetItemKey : null;
    },
    activeSourceKey(): string | null {
      return this.activeRule ? this.activeRule.sourceItemKey : null;
    },
    isDirty(): boolean {
      const saved = this.savedRules
        .map((rule) => `${rule.sourceItemKey}>${rule.targetItemKey}:${rule.mode}`)
        .sort()
        .join("|");
      const draft = this.draftRules
        .map((rule) => `${rule.sourceItemKey}>${rule.targetItemKey}:${rule.mode}`)
        .sort()
        .join("|");
      return saved !== draft;
    },
    brokenCount(): number {
      return this.savedRules.filter((rule) => rule.isBroken).length;
    },
    /** 表で「選択した」ルール (ホバーは含まない) */
    selectedRule(): DisplayRule | null {
      return (
        this.orderedRules.find((rule) => rule.targetItemKey === this.activeRuleTargetKey) || null
      );
    },
    /**
     * 選択バーは「いま何が対応づいて見えているか」を表す。
     * 設定済みルールを選んだときは、グリッドが対を強調しているのに
     * バーだけ「未選択」に見えてしまうため、そのルールの内容を出す。
     *
     * ホバーでは切り替えない。バーの高さが変わると下の表が動き、
     * カーソルから行が外れて mouseleave→mouseenter を繰り返してちらつくため
     * (グリッド側の強調はホバーでも行う。こちらは高さが変わらない)。
     */
    showingActiveRule(): boolean {
      return (
        this.selectedRule !== null &&
        this.selectedSourceKey === null &&
        this.selectedTargetKey === null
      );
    },
    barSourceKey(): string | null {
      if (this.selectedSourceKey !== null) {
        return this.selectedSourceKey;
      }
      return this.showingActiveRule && this.selectedRule ? this.selectedRule.sourceItemKey : null;
    },
    barTargetKey(): string | null {
      if (this.selectedTargetKey !== null) {
        return this.selectedTargetKey;
      }
      return this.showingActiveRule && this.selectedRule ? this.selectedRule.targetItemKey : null;
    },
    barSourceCell(): NurseryTeachingPlanLayoutCell | null {
      return this.barSourceKey === null
        ? null
        : this.sourceCells.find((cell) => cell.itemKey === this.barSourceKey) || null;
    },
    barTargetCell(): NurseryTeachingPlanLayoutCell | null {
      return this.barTargetKey === null
        ? null
        : this.targetCells.find((cell) => cell.itemKey === this.barTargetKey) || null;
    },
    /** 一部の上位レイアウトに転記元の項目が無いルール */
    rulesMissingInSomeSource(): DisplayRule[] {
      if (this.sourceKeysByTemplate.length <= 1) {
        return [];
      }
      return this.orderedRules.filter(
        (rule) => this.missingSourceTemplates(rule.sourceItemKey).length > 0
      );
    },
    /**
     * 欠落を「上位レイアウトごと」にまとめたもの。
     * ルール単位で並べると同じレイアウト名が件数分繰り返されて読み取れないため、
     * 「このレイアウトで作られた帳票からは A・B・C が引けない」の形にする。
     */
    missingBySourceTemplate(): {
      templateId: string;
      name: string;
      symbols: string[];
      itemNames: string[];
    }[] {
      const grouped = new Map<
        string,
        { templateId: string; name: string; symbols: string[]; itemNames: string[] }
      >();
      for (const rule of this.rulesMissingInSomeSource) {
        for (const missing of this.missingSourceTemplates(rule.sourceItemKey)) {
          const entry = grouped.get(missing.templateId) || {
            templateId: missing.templateId,
            name: missing.name,
            symbols: [],
            itemNames: [],
          };
          entry.symbols.push(rule.symbol);
          const itemName = this.sourceCellName(rule.sourceItemKey);
          if (!entry.itemNames.includes(itemName)) {
            entry.itemNames.push(itemName);
          }
          grouped.set(missing.templateId, entry);
        }
      }
      return Array.from(grouped.values());
    },
  },

  watch: {
    templates() {
      if (this.pairTargetPlanType === null || this.targetTemplateId === null) {
        this.initSelection();
      }
    },
  },

  methods: {
    publishedTemplatesOf(planType: NurseryTeachingPlanType) {
      return this.templates.filter(
        (template) => template.planType === planType && template.version !== null
      );
    },
    /** 初期表示。レイアウト編集から渡された転記先があればそれに合わせる */
    initSelection() {
      const requested =
        this.initialTargetTemplateId !== null
          ? this.templates.find(
              (template) =>
                template.templateId === this.initialTargetTemplateId &&
                template.version !== null &&
                adjacentUpperPlanType[template.planType] !== null
            )
          : undefined;
      if (requested) {
        this.pairTargetPlanType = requested.planType;
        this.targetTemplateId = requested.templateId;
        this.load();
        return;
      }
      // 最初に転記先を持てる組み合わせを選ぶ
      for (const item of this.pairItems) {
        const first = this.publishedTemplatesOf(item.value)[0];
        if (first) {
          this.pairTargetPlanType = item.value;
          this.targetTemplateId = first.templateId;
          this.load();
          return;
        }
      }
      this.pairTargetPlanType = this.pairItems.length > 0 ? this.pairItems[0].value : null;
      this.targetTemplateId = null;
    },
    /**
     * 未保存のまま切り替えると黙って消えるため確認する。
     * v-model ではなく :model-value + ハンドラにしているので、
     * ここで false を返せば選択は元の値のまま再描画される。
     */
    async confirmDiscardChanges(): Promise<boolean> {
      if (!this.isDirty) {
        return true;
      }
      return confirmDiscard("保存していない転記ルールの変更があります");
    },
    async onPairChange(next: unknown) {
      const planType = next as NurseryTeachingPlanType;
      if (planType === this.pairTargetPlanType || !(await this.confirmDiscardChanges())) {
        return;
      }
      this.pairTargetPlanType = planType;
      const first = this.publishedTemplatesOf(planType)[0];
      this.applyTarget(first ? first.templateId : null);
    },
    async onTargetTemplateChange(next: unknown) {
      const templateId = next as string;
      if (templateId === this.targetTemplateId || !(await this.confirmDiscardChanges())) {
        return;
      }
      this.applyTarget(templateId);
    },
    applyTarget(templateId: string | null) {
      this.targetTemplateId = templateId;
      if (templateId === null) {
        // 表示中のデータを残すと、別の組み合わせのルールを触っているように見える
        this.loadToken += 1;
        // 進行中の load はトークン不一致で捨てられ loading を戻せないため、ここで戻す
        this.loading = false;
        this.loadFailed = false;
        this.loadedTemplateId = null;
        this.targetLayout = null;
        this.savedRules = [];
        this.draftRules = [];
        this.sourceLayouts = {};
        this.sourceCells = [];
        this.sourceTemplateId = null;
        this.clearSelection();
        this.activeRuleTargetKey = null;
        return;
      }
      this.load();
    },
    isBroken(targetItemKey: string): boolean {
      const rule = this.savedRules.find((item) => item.targetItemKey === targetItemKey);
      return rule ? rule.isBroken : false;
    },
    sourceCellName(itemKey: string): string {
      const cell = this.sourceCells.find((item) => item.itemKey === itemKey);
      return cell ? cell.name : itemKey;
    },
    targetCellName(itemKey: string): string {
      const cell = this.targetCells.find((item) => item.itemKey === itemKey);
      return cell ? cell.name : itemKey;
    },
    /** 表示中の転記元レイアウトに含まれる項目か (含まれないルールも保持し続ける) */
    isInDisplayedSource(itemKey: string): boolean {
      return TeachingPlanUtil.itemKeyOrder(this.sourceLayout).includes(itemKey);
    },
    /** その転記元キーを持つ上位レイアウト */
    sourceTemplatesHaving(sourceItemKey: string): { templateId: string; name: string }[] {
      return this.sourceKeysByTemplate
        .filter((entry) => entry.itemKeys.includes(sourceItemKey))
        .map((entry) => ({ templateId: entry.templateId, name: entry.name }));
    },
    sourceScopeLabel(sourceItemKey: string): string {
      const having = this.sourceTemplatesHaving(sourceItemKey);
      if (having.length === 0) {
        return "どの上位レイアウトにもありません";
      }
      if (having.length === this.sourceKeysByTemplate.length) {
        return `すべての${planTypeLabels[this.sourcePlanType]}にあり`;
      }
      return `${having.map((item) => item.name).join("・")} にあり`;
    },
    /**
     * 表示中の転記元レイアウトにその項目が無いと、対応関係がグリッド上で追えない。
     * 持っているレイアウトへ自動で切り替えて見えるようにする。
     */
    revealSource(sourceItemKey: string) {
      if (this.isInDisplayedSource(sourceItemKey)) {
        return;
      }
      const having = this.sourceTemplatesHaving(sourceItemKey);
      if (having.length > 0) {
        this.sourceTemplateId = having[0].templateId;
      }
    },
    /** 転記先レイアウトに残っている項目か (消えているとルールが要確認になる) */
    isInTargetLayout(itemKey: string): boolean {
      return this.targetCells.some((cell) => cell.itemKey === itemKey);
    },
    /**
     * その転記元キーを持たない上位レイアウト。
     * ここに挙がったレイアウトで作られた上位帳票からは、実行時に値を引けない
     * (画面では「転記元の帳票にこの項目がありません」として現れる)。
     */
    missingSourceTemplates(sourceItemKey: string): { templateId: string; name: string }[] {
      return this.sourceKeysByTemplate
        .filter((entry) => !entry.itemKeys.includes(sourceItemKey))
        .map((entry) => ({ templateId: entry.templateId, name: entry.name }));
    },
    missingSourceLabel(sourceItemKey: string): string {
      const missing = this.missingSourceTemplates(sourceItemKey);
      if (missing.length === 0) {
        return "";
      }
      return `${missing.map((item) => item.name).join("・")} にこの項目がありません`;
    },
    /**
     * 転記先レイアウトを素早く切り替えると応答が前後しうる。
     * 保存は「転記先レイアウト単位の一括置換」で、別レイアウトのルールを流し込むと
     * 既存ルールを壊すため、リクエストトークンで自分の応答だけを反映する。
     */
    async load() {
      const requestedId = this.targetTemplateId;
      if (!requestedId) {
        return;
      }
      this.loadToken += 1;
      const token = this.loadToken;
      this.loading = true;
      this.loadFailed = false;
      this.clearSelection();
      this.activeRuleTargetKey = null;
      this.brokenOnly = false;
      const [target, rules] = await Promise.all([
        ApiTeachingPlan.findTemplate({ templateId: requestedId }).catch(() => null),
        ApiTeachingPlan.listTransferRules({
          nurseryId: this.nurseryId,
          targetTemplateId: requestedId,
        }).catch(() => null),
      ]);
      if (token !== this.loadToken) {
        return;
      }
      if (target == null || rules == null) {
        // 取得失敗を「ルール0件」と区別する。0件表示のまま保存できると
        // レイアウト単位の一括置換で既存ルールを消してしまうため、編集を封鎖する
        this.loadFailed = true;
        this.loading = false;
        return;
      }
      this.targetLayout = target.layout;
      this.savedRules = rules.data;
      this.draftRules = this.savedRules.map((rule) => ({
        sourceItemKey: rule.sourceItemKey,
        targetItemKey: rule.targetItemKey,
        mode: rule.mode,
      }));
      const loaded = await this.fetchSourceLayouts();
      if (token !== this.loadToken) {
        return;
      }
      if (loaded == null) {
        this.loadFailed = true;
        this.loading = false;
        return;
      }
      this.sourceLayouts = loaded.layouts;
      this.sourceCells = loaded.cells;
      this.sourceTemplateId = this.pickSourceTemplateId(loaded.layouts);
      this.loadedTemplateId = requestedId;
      this.loading = false;
    },
    /**
     * 転記元の候補は「その帳票種別のいずれかの公開レイアウトに存在する項目」の集合
     * (サーバ側の検証と同じ範囲。実行時の突合は sourceMissing に委ねられる)。
     * グリッド描画のためレイアウトそのものも保持する。
     */
    async fetchSourceLayouts(): Promise<{
      layouts: { [templateId: string]: NurseryTeachingPlanLayout };
      cells: NurseryTeachingPlanLayoutCell[];
    } | null> {
      const sourceTemplates = this.templates.filter(
        (template) => template.planType === this.sourcePlanType && template.version !== null
      );
      const responses = await Promise.all(
        sourceTemplates.map((template) =>
          ApiTeachingPlan.findTemplate({ templateId: template.templateId })
            .then((response) => ({ templateId: template.templateId, response }))
            .catch(() => null)
        )
      );
      // 一部の転記元レイアウトだけ取得に失敗すると、そのレイアウトのルールが欠けて見え、
      // そのまま保存すると消えてしまう。全件取得できたときだけ成功として扱う
      if (responses.some((item) => item == null)) {
        return null;
      }
      const layouts: { [templateId: string]: NurseryTeachingPlanLayout } = {};
      const cells: NurseryTeachingPlanLayoutCell[] = [];
      const seen = new Set<string>();
      for (const item of responses) {
        if (!item || !item.response || !item.response.layout) {
          continue;
        }
        layouts[item.templateId] = item.response.layout;
        for (const cell of TeachingPlanUtil.collectInputCells(item.response.layout)) {
          if (cell.itemKey && !seen.has(cell.itemKey)) {
            seen.add(cell.itemKey);
            cells.push(cell);
          }
        }
      }
      return { layouts, cells };
    },
    /** 既存ルールの転記元を最も多く含むレイアウトを初期表示にする */
    pickSourceTemplateId(layouts: {
      [templateId: string]: NurseryTeachingPlanLayout;
    }): string | null {
      const templateIds = Object.keys(layouts);
      if (templateIds.length === 0) {
        return null;
      }
      let best = templateIds[0];
      let bestScore = -1;
      for (const templateId of templateIds) {
        const keys = TeachingPlanUtil.itemKeyOrder(layouts[templateId]);
        const score = this.draftRules.filter((rule) =>
          keys.includes(rule.sourceItemKey)
        ).length;
        if (score > bestScore) {
          best = templateId;
          bestScore = score;
        }
      }
      return best;
    },
    clearSelection() {
      this.selectedSourceKey = null;
      this.selectedTargetKey = null;
    },
    async selectSource(itemKey: string) {
      // 保存中の編集は成功応答の draftRules 上書きで黙って巻き戻るため受け付けない
      if (this.saving) {
        return;
      }
      this.selectedSourceKey = this.selectedSourceKey === itemKey ? null : itemKey;
      await this.pairIfReady();
    },
    async selectTarget(itemKey: string) {
      if (this.saving) {
        return;
      }
      if (this.selectedTargetKey === itemKey) {
        this.selectedTargetKey = null;
        return;
      }
      this.selectedTargetKey = itemKey;
      if (this.selectedSourceKey) {
        await this.pairIfReady();
        return;
      }
      // 転記元が未選択なら、既に設定済みのルールを選んだものとして扱う
      const existing = this.draftRules.find((rule) => rule.targetItemKey === itemKey);
      this.activeRuleTargetKey = existing ? itemKey : null;
      if (existing) {
        // そのルールの転記元が別レイアウトにあるなら切り替えて見えるようにする
        this.revealSource(existing.sourceItemKey);
      }
    },
    /** 両側が選ばれた時点で対応づける (転記先1セルにつき1ルール) */
    async pairIfReady() {
      const sourceItemKey = this.selectedSourceKey;
      const targetItemKey = this.selectedTargetKey;
      const mode = this.derivedMode;
      if (!sourceItemKey || !targetItemKey || !mode) {
        return;
      }
      const existing = this.draftRules.find((rule) => rule.targetItemKey === targetItemKey);
      if (existing && existing.sourceItemKey === sourceItemKey && existing.mode === mode) {
        this.clearSelection();
        this.activeRuleTargetKey = targetItemKey;
        return;
      }
      if (existing) {
        const templateId = this.targetTemplateId;
        const confirmed = await confirmAction(
          `「${this.targetCellName(targetItemKey)}」の転記元を置き換えますか？`,
          `いまは「${this.sourceCellName(existing.sourceItemKey)}」から転記する設定です。`,
          "置き換える"
        );
        // ダイアログを閉じる間に読み込み直し・レイアウト切替が起きると、
        // 別のルール集合に対して置き換えを走らせてしまう
        if (
          !confirmed ||
          this.targetTemplateId !== templateId ||
          this.draftRules.find((rule) => rule.targetItemKey === targetItemKey) !== existing
        ) {
          this.clearSelection();
          return;
        }
      }
      this.draftRules = this.draftRules.filter((rule) => rule.targetItemKey !== targetItemKey);
      this.draftRules.push({ sourceItemKey, targetItemKey, mode });
      this.clearSelection();
      this.activeRuleTargetKey = targetItemKey;
    },
    focusRule(targetItemKey: string) {
      const next = this.activeRuleTargetKey === targetItemKey ? null : targetItemKey;
      this.activeRuleTargetKey = next;
      if (next === null) {
        return;
      }
      // 選んだルールの転記元がグリッドで見えるようにする
      const rule = this.draftRules.find((item) => item.targetItemKey === next);
      if (rule) {
        this.revealSource(rule.sourceItemKey);
      }
    },
    removeRule(targetItemKey: string) {
      if (this.saving) {
        return;
      }
      this.draftRules = this.draftRules.filter((rule) => rule.targetItemKey !== targetItemKey);
      if (this.activeRuleTargetKey === targetItemKey) {
        this.activeRuleTargetKey = null;
      }
      if (this.hoveredRuleTargetKey === targetItemKey) {
        this.hoveredRuleTargetKey = null;
      }
    },
    async save() {
      // 表示中のレイアウトと draftRules の出どころが一致しないまま保存すると、
      // 一括置換なので別レイアウトのルールを消してしまう
      if (!this.targetTemplateId || this.loading || this.loadedTemplateId !== this.targetTemplateId) {
        return;
      }
      const targetTemplateId = this.targetTemplateId;
      // 保存応答が返る間にレイアウトを切り替えられると、応答で別レイアウトの
      // ルールを上書きしてしまう。読み込みと同じトークンで世代を照合する
      const token = this.loadToken;
      this.saving = true;
      const response = await ApiTeachingPlan.upsertTransferRules({
        nurseryId: this.nurseryId,
        sourcePlanType: this.sourcePlanType,
        targetTemplateId,
        rules: this.draftRules,
      }).catch(() => null);
      this.saving = false;
      if (token !== this.loadToken || this.targetTemplateId !== targetTemplateId) {
        return;
      }
      if (response) {
        this.savedRules = response.data;
        this.draftRules = response.data.map((rule) => ({
          sourceItemKey: rule.sourceItemKey,
          targetItemKey: rule.targetItemKey,
          mode: rule.mode,
        }));
        notifySuccess("転記ルールを保存しました");
      }
    },
  },
});
</script>

<style scoped>
.tp-rule-bar {
  background-color: #fbfdff;
}
.tp-rule-bar__slot {
  min-width: 150px;
  /* 選択の有無で高さが変わると、下の表が動いてホバーがばたつく */
  min-height: 56px;
}
.tp-rule-bar__value {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}
.tp-rule-bar__placeholder {
  font-size: 13px;
  color: #b0bec5;
}
.tp-legend {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}
.tp-legend--carry {
  background-color: #cfe8f5;
  color: #01579b;
}
.tp-legend--reference {
  background-color: #1565c0;
  color: #ffffff;
}
.tp-legend--broken {
  background-color: #ef6c00;
  color: #ffffff;
}
.tp-rule-row {
  cursor: pointer;
}
.tp-rule-row--active {
  background-color: #e3f2fd;
}
</style>
